import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiImage, FiX } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import teamService from '../../services/teamService';
import { UpdateTeamMemberData } from '../../types/team.types';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';
import Card from '../../components/common/Card/Card';
import Loader from '../../components/common/Loader/Loader';
import { useAuth } from '../../context/AuthContext';
import styles from './CreateTeam.module.css';

interface FormData {
  name: string;
  email: string;
  mobile: string;
  role: string;
  isActive: boolean;
  profilePhoto: File | null;
}

const EditTeam: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [originalData, setOriginalData] = useState<FormData | null>(null);
  const [memberRole, setMemberRole] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    mobile: '',
    role: 'blogger',
    isActive: true,
    profilePhoto: null,
  });

  useEffect(() => {
    if (id) {
      fetchTeamMember(id);
    } else {
      toast.error('Invalid team member ID');
      navigate('/team');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTeamMember = async (memberId: string) => {
    try {
      setLoading(true);
      console.log('📖 Fetching team member with ID:', memberId);

      const member = await teamService.getTeamMemberById(memberId);
      console.log('✅ Team member loaded:', member.name);

      setMemberRole(member.role);

      const initialData: FormData = {
        name: member.name,
        email: member.email,
        mobile: member.mobile,
        role: member.role,
        isActive: member.isActive,
        profilePhoto: null,
      };

      setFormData(initialData);
      setOriginalData(initialData);
      setImagePreview(member.profilePhoto?.url || '');
    } catch (error: any) {
      console.error('❌ Failed to fetch team member:', error);
      toast.error(error.response?.data?.message || 'Failed to load team member');
      navigate('/team');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Only JPG, PNG, and WebP images are allowed');
        return;
      }

      setFormData((prev) => ({ ...prev, profilePhoto: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      console.log('✅ New profile photo selected:', file.name);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, profilePhoto: null }));
    if (id) {
      fetchTeamMember(id);
    }
  };

  const hasChanges = (): boolean => {
    if (!originalData) return false;

    return (
      formData.name !== originalData.name ||
      formData.email !== originalData.email ||
      formData.mobile !== originalData.mobile ||
      formData.role !== originalData.role ||
      formData.isActive !== originalData.isActive ||
      formData.profilePhoto !== null
    );
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim() || formData.name.length < 2 || formData.name.length > 50) {
      toast.error('Name must be between 2 and 50 characters');
      return false;
    }

    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please provide a valid email address');
      return false;
    }

    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(formData.mobile)) {
      toast.error('Please provide a valid 10-digit Indian mobile number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!id) {
      toast.error('Team member ID is missing');
      return;
    }

    if (!hasChanges()) {
      toast('No changes to save');
      return;
    }

    try {
      setSaving(true);
      console.log('💾 Updating team member with ID:', id);

      const submitData: UpdateTeamMemberData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        mobile: formData.mobile.trim(),
      };

      // Only superadmin can change role and active status
      if (user?.role === 'superadmin') {
        submitData.role = formData.role as any;
        submitData.isActive = formData.isActive;
      }

      if (formData.profilePhoto) {
        submitData.profilePhoto = formData.profilePhoto;
      }

      await teamService.updateTeamMember(id, submitData);

      toast.success('Team member updated successfully!');
      navigate('/team');
    } catch (error: any) {
      console.error('❌ Failed to update team member:', error);

      let errorMessage = 'Failed to update team member';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (Array.isArray(errors)) {
          errorMessage = errors.map((e: any) => e.message).join(', ');
        } else if (typeof errors === 'object') {
          errorMessage = Object.values(errors).join(', ');
        }
      }

      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) return;
    }
    navigate('/team');
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader size="large" text="Loading team member..." />
      </div>
    );
  }

  // Prevent editing SuperAdmin
  if (memberRole === 'superadmin' && user?.role !== 'superadmin') {
    return (
      <div className={styles.loaderContainer}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'var(--text-primary)', marginBottom: 'var(--spacing-lg)' }}>
            Access Denied
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-xl)' }}>
            Only SuperAdmin can edit SuperAdmin accounts.
          </p>
          <Button variant="primary" onClick={() => navigate('/team')}>
            Back to Team
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createTeam}>
      <div className={styles.header}>
        <Button variant="ghost" icon={<FiArrowLeft />} onClick={handleCancel}>
          Back to Team
        </Button>
        <h1 className={styles.title}>Edit Team Member</h1>
        <p className={styles.subtitle}>Update team member information and permissions</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Profile Photo */}
        <Card title="Profile Photo" className={styles.section}>
          <div className={styles.imageUpload}>
            {imagePreview ? (
              <div className={styles.imagePreviewContainer}>
                <img src={imagePreview} alt="Preview" className={styles.imagePreview} />
                <div className={styles.imageActions}>
                  <label className={styles.changeImage}>
                    <FiImage /> Change Photo
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageChange}
                      className={styles.fileInput}
                    />
                  </label>
                  {formData.profilePhoto && (
                    <button type="button" className={styles.removeImage} onClick={removeImage}>
                      <FiX /> Remove New Photo
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <label className={styles.imageUploadLabel}>
                <FiImage className={styles.uploadIcon} />
                <p className={styles.uploadText}>Click to upload profile photo</p>
                <p className={styles.uploadHint}>PNG, JPG, WebP up to 5MB</p>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageChange}
                  className={styles.fileInput}
                />
              </label>
            )}
          </div>
        </Card>

        {/* Basic Information */}
        <Card title="Basic Information" className={styles.section}>
          <div className={styles.formGrid}>
            <div className={styles.fullWidth}>
              <Input
                name="name"
                label="Full Name *"
                placeholder="Enter full name"
                value={formData.name}
                onChange={handleInputChange}
                required
                fullWidth
                maxLength={50}
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  name="email"
                  type="email"
                  label="Email Address *"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </div>

              <div className={styles.formGroup}>
                <Input
                  name="mobile"
                  type="tel"
                  label="Mobile Number *"
                  placeholder="10-digit number"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  maxLength={10}
                />
              </div>
            </div>

            {user?.role === 'superadmin' && (
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Role *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className={styles.select}
                    required
                    disabled={memberRole === 'superadmin'}
                  >
                    <option value="blogger">Blogger</option>
                    <option value="editor">Editor</option>
                    <option value="manager">Manager</option>
                    <option value="executive">Executive</option>
                    <option value="developer">Developer</option>
                    <option value="affiliater">Affiliater</option>
                    <option value="admin">Admin</option>
                    {memberRole === 'superadmin' && <option value="superadmin">Super Admin</option>}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Account Status</label>
                  <div className={styles.checkboxGroup}>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        disabled={memberRole === 'superadmin'}
                      />
                      <span>Active Account</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <Button type="button" variant="ghost" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            icon={<FiSave />}
            loading={saving}
            disabled={saving || !hasChanges()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {hasChanges() && !saving && (
          <div className={styles.unsavedChanges}>⚠️ You have unsaved changes</div>
        )}
      </form>
    </div>
  );
};

export default EditTeam;